import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import ReactSwitch from 'react-switch';
import { useAtomValue } from 'jotai/utils';
import {
  AdminSettingsSection,
  Button,
  FeatureUnavailableBanner,
  Loader,
  PageBody,
  SettingsNavigation,
  SaveChangesBar,
} from 'src/components';
import { Input } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import { useAiSettingsQuery } from 'src/api/queries/aiSettingsQueries';
import { useUpdateAiSettingsMutation } from 'src/api/mutations/aiSettingsMutations';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { AiSettingsUpdatePayload } from 'src/types/TAiSettings';
import styles from './styles.module.scss';

const ALL_ROLES = ['ADMIN', 'MANAGER', 'USER', 'CREATOR'];

export const AiSettings: React.FC = () => {
  const { showSuccess, showAlert } = useAlerts();
  const { data, isLoading, error } = useAiSettingsQuery();
  const updateMutation = useUpdateAiSettingsMutation();

  // Built-in feature: the page always renders, but goes read-only (all
  // controls disabled, "Not enabled" banner) when the account lacks the AI
  // addon or the backend AI APIs are unavailable (404 on the old release).
  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const aiAddonEnabled = Boolean(
    addonsSettings?.AI_ASSISTANT?.added && addonsSettings?.AI_ASSISTANT?.enabled,
  );
  const featureStatus = useFeatureAvailability('ai-agent');
  const errorStatus = error && isErrorResponseResult(error) ? error.status : undefined;
  const unavailable =
    !aiAddonEnabled || featureStatus === 'unavailable' || errorStatus === 404;

  const [enabled, setEnabled] = useState(false);
  const [imageEditEnabled, setImageEditEnabled] = useState(false);
  const [allowedRoles, setAllowedRoles] = useState<string[]>([]);
  const [rateLimitRpm, setRateLimitRpm] = useState('30');
  const [dailyCreditLimit, setDailyCreditLimit] = useState('0');
  const [weeklyCreditLimit, setWeeklyCreditLimit] = useState('0');
  const [monthlyCreditLimit, setMonthlyCreditLimit] = useState('0');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setImageEditEnabled(data.image_edit_enabled ?? false);
      setAllowedRoles(data.allowed_roles || []);
      setRateLimitRpm(String(data.rate_limit_rpm ?? 30));
      setDailyCreditLimit(String(data.daily_credit_limit ?? 0));
      setWeeklyCreditLimit(String(data.weekly_credit_limit ?? 0));
      setMonthlyCreditLimit(String(data.monthly_credit_limit ?? 0));
      setDirty(false);
    }
  }, [data]);

  const markDirty = () => setDirty(true);

  const toggleRole = (role: string) => {
    setAllowedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
    markDirty();
  };

  const handleSave = async () => {
    const payload: AiSettingsUpdatePayload = {
      enabled,
      image_edit_enabled: imageEditEnabled,
      allowed_roles: allowedRoles,
      rate_limit_rpm: parseInt(rateLimitRpm, 10) || 30,
      daily_credit_limit: parseInt(dailyCreditLimit, 10) || 0,
      weekly_credit_limit: parseInt(weeklyCreditLimit, 10) || 0,
      monthly_credit_limit: parseInt(monthlyCreditLimit, 10) || 0,
    };

    try {
      await updateMutation.mutateAsync(payload);
      showSuccess('AI Agent settings updated');
      setDirty(false);
    } catch {
      showAlert('Failed to update AI Agent settings');
    }
  };

  const handleReset = () => {
    if (data) {
      setEnabled(data.enabled);
      setImageEditEnabled(data.image_edit_enabled ?? false);
      setAllowedRoles(data.allowed_roles || []);
      setRateLimitRpm(String(data.rate_limit_rpm ?? 30));
      setDailyCreditLimit(String(data.daily_credit_limit ?? 0));
      setWeeklyCreditLimit(String(data.weekly_credit_limit ?? 0));
      setMonthlyCreditLimit(String(data.monthly_credit_limit ?? 0));
      setDirty(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        <SettingsNavigation />
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      </div>
    );
  }

  // Only surface a hard error for genuine failures — an "unavailable" error
  // (addon off / API missing) renders read-only below instead.
  if (error && !unavailable) {
    return (
      <div className={styles.pageWrapper}>
        <SettingsNavigation />
        <div className={styles.errorBanner}>
          Failed to load AI Agent settings. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <SettingsNavigation />
      <SaveChangesBar />

      <PageBody narrow>
        {unavailable && <FeatureUnavailableBanner />}

        <p className={styles.scopeAlert}>
          This setting applies to all users administered under this account.
        </p>
        <AdminSettingsSection title="AI Agent">
          <p className={styles.sectionDescription}>
            Configure AI Agent access and credit limits for your account.
          </p>

          <div className={styles.fieldGroup}>
            <div className={styles.field}>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Enable AI Agent</span>
                <ReactSwitch
                  checked={enabled}
                  disabled={unavailable}
                  onChange={(checked) => {
                    setEnabled(checked);
                    markDirty();
                  }}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#2e53e3"
                  width={40}
                  height={20}
                  handleDiameter={16}
                />
              </div>
              <span className={styles.fieldHint}>
                When disabled, no users in this account can access the AI Agent.
              </span>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Allowed Roles</span>
              <div className={styles.rolesGrid}>
                {ALL_ROLES.map((role) => (
                  <button
                    key={role}
                    type="button"
                    className={cn(styles.roleChip, {
                      [styles.roleChipActive]: allowedRoles.includes(role),
                    })}
                    onClick={() => toggleRole(role)}
                    disabled={unavailable}
                  >
                    {role}
                  </button>
                ))}
              </div>
              <span className={styles.fieldHint}>
                Select which roles can use the AI Agent.
              </span>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Requests Per Minute</span>
              <Input
                type="number"
                variant="secondary"
                value={rateLimitRpm}
                min={1}
                placeholder="30"
                disabled={unavailable}
                onChange={(val) => {
                  setRateLimitRpm(val);
                  markDirty();
                }}
              />
              <span className={styles.fieldHint}>
                Maximum chat requests per minute per user.
              </span>
            </div>
          </div>
        </AdminSettingsSection>

        <AdminSettingsSection title="AI Image Edit">
          <p className={styles.sectionDescription}>
            AI-powered photo editing and generation (enhancement, background removal,
            generative fill/expand, text-to-image). Defaults to off.
          </p>

          <div className={styles.fieldGroup}>
            <div className={styles.field}>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Enable AI Image Edit</span>
                <ReactSwitch
                  checked={imageEditEnabled}
                  disabled={unavailable}
                  onChange={(checked) => {
                    setImageEditEnabled(checked);
                    markDirty();
                  }}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#2e53e3"
                  width={40}
                  height={20}
                  handleDiameter={16}
                />
              </div>
              <span className={styles.fieldHint}>
                Account master switch. When enabled, individual users still need the
                AI Image Edit addon granted to them before they can run edits.
              </span>
            </div>
          </div>
        </AdminSettingsSection>

        <AdminSettingsSection title="Credit Limits">
          <p className={styles.sectionDescription}>
            Limit how fast credits can be consumed. The credit pool balance is managed by FileSpin support.
            Set to 0 for no limit.
          </p>

          <div className={styles.fieldGroup}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Daily Credit Limit</span>
              <Input
                type="number"
                variant="secondary"
                value={dailyCreditLimit}
                min={0}
                placeholder="0"
                disabled={unavailable}
                onChange={(val) => {
                  setDailyCreditLimit(val);
                  markDirty();
                }}
              />
              <span className={styles.fieldHint}>
                Max credits consumable per day. 0 = no daily cap.
              </span>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Weekly Credit Limit</span>
              <Input
                type="number"
                variant="secondary"
                value={weeklyCreditLimit}
                min={0}
                placeholder="0"
                disabled={unavailable}
                onChange={(val) => {
                  setWeeklyCreditLimit(val);
                  markDirty();
                }}
              />
              <span className={styles.fieldHint}>
                Max credits consumable per week. 0 = no weekly cap.
              </span>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Monthly Credit Limit</span>
              <Input
                type="number"
                variant="secondary"
                value={monthlyCreditLimit}
                min={0}
                placeholder="0"
                disabled={unavailable}
                onChange={(val) => {
                  setMonthlyCreditLimit(val);
                  markDirty();
                }}
              />
              <span className={styles.fieldHint}>
                Max credits consumable per month. 0 = no monthly cap.
              </span>
            </div>
          </div>
        </AdminSettingsSection>

        <div className={styles.actions}>
          <Button
            mode="primary"
            size="small"
            onClick={handleSave}
            disabled={unavailable || !dirty || updateMutation.isLoading}
          >
            {updateMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
          <Button
            mode="tertiary"
            size="small"
            onClick={handleReset}
            disabled={unavailable || !dirty}
          >
            Reset
          </Button>
        </div>
      </PageBody>
    </div>
  );
};

export default AiSettings;
